/**
 * Data hooks for the patients enrolled on an EHR connection, over
 * engage-v2-backend's `ehrSyncPatientLinks`, `ehrSyncEnrollCandidates`, the
 * review queue (`listEhrPatientLinksForReview`, `reviewEhrPatientLink`) and the
 * enrol, pause/resume and unlink mutations. Maps rows onto @models/ehr-sync.
 */
import type { ApolloError, FetchResult } from '@apollo/client';
import type { GraphQLError } from 'graphql';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useResponseHandler } from '@hooks/useResponseHandler';

import type {
  EhrEnrollCandidate,
  EhrMatchField as UiMatchField,
  EhrMatchFieldResult as UiMatchFieldResult,
  EhrPatientLink,
  EhrPatientLinkStatus,
  EhrPatientSummary,
  EhrReviewCase,
} from '@models/ehr-sync';

import {
  type EhrPatientLinkFieldsFragment,
  type EhrPatientSummaryFieldsFragment,
  useEhrSyncEnrollCandidatesQuery,
  useEhrSyncPatientLinksQuery,
  useEnrollEhrSyncPatientMutation,
  useListEhrPatientLinksForReviewQuery,
  useReviewEhrPatientLinkMutation,
  type ListEhrPatientLinksForReviewQuery,
  useSetEhrPatientLinkPausedMutation,
  useUnenrollEhrSyncPatientMutation,
} from 'generated/ehr-sync.generated';
import {
  EhrPatientLinkStatus as ApiLinkStatus,
  EhrMatchField,
  EhrMatchFieldResult,
  EhrPatientReviewDecision,
} from 'generated/types';

/** Shortest query the enrol search sends; the backend rejects shorter ones. */
export const MIN_ENROLL_QUERY_LENGTH = 2;

const LINK_STATUS: Record<ApiLinkStatus, EhrPatientLinkStatus> = {
  [ApiLinkStatus.Pending]: 'pending',
  [ApiLinkStatus.NeedsReview]: 'needsReview',
  [ApiLinkStatus.AutoLinked]: 'autoLinked',
  [ApiLinkStatus.Linked]: 'linked',
  [ApiLinkStatus.Rejected]: 'rejected',
  [ApiLinkStatus.NoMatch]: 'noMatch',
  [ApiLinkStatus.Paused]: 'paused',
  [ApiLinkStatus.Unlinked]: 'unlinked',
};

const mapSummary = (p: EhrPatientSummaryFieldsFragment): EhrPatientSummary => ({
  vitallPatientId: p.vitall_patient_id,
  firstName: p.first_name ?? null,
  lastName: p.last_name ?? null,
  vitallUserId: p.vitall_user_id ?? null,
  birthDate: p.birth_date ?? null,
  gender: p.gender ?? null,
});

/**
 * Maps a backend link onto the UI model.
 * @param l A link as returned by the GraphQL API
 * @returns The camelCase UI model
 */
export const mapEhrPatientLink = (
  l: EhrPatientLinkFieldsFragment
): EhrPatientLink => ({
  id: l.id,
  ehrConnectionId: l.ehr_connection_id,
  vitallPatientId: l.vitall_patient_id,
  externalPatientId: l.external_patient_id ?? null,
  status: LINK_STATUS[l.status],
  matchScore: l.match_score ?? null,
  matchAlgorithm: l.match_algorithm ?? null,
  enrolledByName: l.enrolled_by_name ?? null,
  reviewedByName: l.reviewed_by_name ?? null,
  reviewedAt: l.reviewed_at ?? null,
  createdAt: l.created_at,
  updatedAt: l.updated_at,
  patient: l.patient ? mapSummary(l.patient) : null,
});

interface UseEhrSyncPatientLinksResult {
  data: EhrPatientLink[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * The patient links of one connection. Skipped while `ehrConnectionId` is
 * null, which lets a caller defer the fetch.
 * @param ehrConnectionId The connection's registry id
 * @returns The mapped links, newest first, plus loading, error and refetch
 */
export const useEhrSyncPatientLinks = (
  ehrConnectionId?: string | null
): UseEhrSyncPatientLinksResult => {
  const response = useEhrSyncPatientLinksQuery({
    variables: { input: { ehr_connection_id: ehrConnectionId ?? '' } },
    skip: !ehrConnectionId,
    notifyOnNetworkStatusChange: true,
  });
  const data = useMemo(
    () => (response.data?.ehrSyncPatientLinks ?? []).map(mapEhrPatientLink),
    [response.data]
  );
  return {
    data,
    loading: response.loading,
    error: response.error,
    refetch: () => {
      void response.refetch();
    },
  };
};

/**
 * Enrol candidates for a search. Skipped for queries shorter than
 * {@link MIN_ENROLL_QUERY_LENGTH}; callers SHOULD debounce the query.
 * @param ehrConnectionId The connection's registry id
 * @param query The search text
 * @returns The candidates and a loading flag
 */
export const useEhrSyncEnrollCandidates = (
  ehrConnectionId: string,
  query: string
): { data: EhrEnrollCandidate[]; loading: boolean } => {
  const trimmed = query.trim();
  const response = useEhrSyncEnrollCandidatesQuery({
    variables: {
      input: { ehr_connection_id: ehrConnectionId, query: trimmed },
    },
    skip: trimmed.length < MIN_ENROLL_QUERY_LENGTH,
    fetchPolicy: 'network-only',
  });
  const data = useMemo(
    () =>
      (response.data?.ehrSyncEnrollCandidates ?? []).map((c) => ({
        patient: mapSummary(c.patient),
        alreadyEnrolled: c.already_enrolled,
      })),
    [response.data]
  );
  return { data, loading: response.loading };
};

/** Mutation options shared by the link writes: refresh the list afterwards. */
const REFETCH_LINKS = {
  refetchQueries: ['ehrSyncPatientLinks'],
  awaitRefetchQueries: true,
};

type WriteError = ApolloError | ReadonlyArray<GraphQLError>;

/**
 * Snackbar error handler for the link writes. An error `parseError` cannot
 * read, such as a network failure, gets the generic message rather than none.
 * @returns The handler
 */
const useWriteErrorHandler = () => {
  const { handleError, parseError, enqueueSnackbar } = useResponseHandler({
    handlerType: 'snackbar',
  });
  const { formatMessage } = useIntl();
  return (error: WriteError) => {
    if (parseError(error)) {
      handleError(error);
      return;
    }
    enqueueSnackbar({
      variant: 'error',
      message: formatMessage({ id: 'errorMessage.default' }),
    });
  };
};

/**
 * Runs a link mutation, sending errors to the snackbar.
 * @param write Sends the mutation
 * @param succeeded Reads success from the payload. A boolean payload MUST be
 *   read by value, since the data wrapper itself is always truthy.
 * @param handleError The snackbar error handler
 * @returns true when the mutation succeeded
 */
const runWrite = async <TData>(
  write: () => Promise<FetchResult<TData>>,
  succeeded: (data: TData) => boolean,
  handleError: (error: WriteError) => void
): Promise<boolean> => {
  try {
    const { data, errors } = await write();
    if (errors) {
      handleError(errors as ReadonlyArray<GraphQLError>);
      return false;
    }
    return data ? succeeded(data) : false;
  } catch (error) {
    handleError(error as ApolloError);
    return false;
  }
};

/**
 * Enrols a patient on a connection. The link starts Pending and goes through
 * matching whether or not an external id is given.
 * @returns The enrol function, resolving true on success, and a loading flag
 */
export const useEnrollEhrSyncPatient = (): [
  (args: {
    ehrConnectionId: string;
    vitallPatientId: string;
    externalPatientId?: string;
  }) => Promise<boolean>,
  boolean,
] => {
  const handleError = useWriteErrorHandler();
  const [mutate, { loading }] = useEnrollEhrSyncPatientMutation(REFETCH_LINKS);
  const enroll = ({
    ehrConnectionId,
    vitallPatientId,
    externalPatientId,
  }: {
    ehrConnectionId: string;
    vitallPatientId: string;
    externalPatientId?: string;
  }) =>
    runWrite(
      () =>
        mutate({
          variables: {
            input: {
              ehr_connection_id: ehrConnectionId,
              vitall_patient_id: vitallPatientId,
              external_patient_id: externalPatientId?.trim() || undefined,
            },
          },
        }),
      (data) => Boolean(data.enrollEhrSyncPatient),
      handleError
    );
  return [enroll, loading];
};

/**
 * Pauses or resumes a link. Resume restores the status the link had before
 * it was paused.
 * @returns The setter, resolving true on success, and a loading flag
 */
export const useSetEhrPatientLinkPaused = (): [
  (linkId: string, paused: boolean) => Promise<boolean>,
  boolean,
] => {
  const handleError = useWriteErrorHandler();
  const [mutate, { loading }] =
    useSetEhrPatientLinkPausedMutation(REFETCH_LINKS);
  const setPaused = (linkId: string, paused: boolean) =>
    runWrite(
      () => mutate({ variables: { input: { link_id: linkId, paused } } }),
      (data) => Boolean(data.setEhrPatientLinkPaused),
      handleError
    );
  return [setPaused, loading];
};

/**
 * Unlinks a patient. The row is kept with status Unlinked.
 * @returns The unlink function, resolving true on success, and a loading flag
 */
export const useUnenrollEhrSyncPatient = (): [
  (linkId: string) => Promise<boolean>,
  boolean,
] => {
  const handleError = useWriteErrorHandler();
  const [mutate, { loading }] = useUnenrollEhrSyncPatientMutation({
    // Unlinking a NeedsReview link drops it from the review queue and from
    // getEhrSyncConnection's review_count.
    refetchQueries: [
      'ehrSyncPatientLinks',
      'listEhrPatientLinksForReview',
      'getEhrSyncConnection',
    ],
    awaitRefetchQueries: true,
  });
  const unenroll = (linkId: string) =>
    runWrite(
      () => mutate({ variables: { input: { link_id: linkId } } }),
      (data) => data.unenrollEhrSyncPatient,
      handleError
    );
  return [unenroll, loading];
};

const MATCH_FIELD: Record<EhrMatchField, UiMatchField> = {
  [EhrMatchField.Family]: 'family',
  [EhrMatchField.Given]: 'given',
  [EhrMatchField.BirthDate]: 'birthDate',
  [EhrMatchField.Gender]: 'gender',
  [EhrMatchField.Phone]: 'phone',
  [EhrMatchField.Address]: 'address',
  [EhrMatchField.PostalCode]: 'postalCode',
  [EhrMatchField.Email]: 'email',
};

const MATCH_RESULT: Record<EhrMatchFieldResult, UiMatchFieldResult> = {
  [EhrMatchFieldResult.Exact]: 'exact',
  [EhrMatchFieldResult.Close]: 'close',
  [EhrMatchFieldResult.Different]: 'different',
  [EhrMatchFieldResult.Missing]: 'missing',
};

type RawReviewCase =
  ListEhrPatientLinksForReviewQuery['listEhrPatientLinksForReview'][number];

/**
 * Maps a backend review case onto the UI model.
 * @param c A case as returned by the GraphQL API
 * @returns The camelCase UI model, fields keyed by name
 */
export const mapEhrReviewCase = (c: RawReviewCase): EhrReviewCase => ({
  link: mapEhrPatientLink(c.link),
  matchedAt: c.matched_at,
  algorithm: c.algorithm ?? null,
  bestScore: c.best_score ?? null,
  autoLinkThreshold: c.auto_link_threshold,
  noMatchThreshold: c.no_match_threshold,
  subject: Object.fromEntries(
    c.subject.map((s) => [MATCH_FIELD[s.field], s.value ?? null])
  ),
  candidates: c.candidates.map((candidate) => ({
    externalPatientId: candidate.external_patient_id,
    score: candidate.score,
    fields: Object.fromEntries(
      candidate.fields.map((f) => [
        MATCH_FIELD[f.field],
        { value: f.value ?? null, result: MATCH_RESULT[f.result] },
      ])
    ),
  })),
});

/**
 * The review queue of one connection: NeedsReview links, oldest match first.
 * Skipped until the connection has a registry row.
 * @param ehrConnectionId The connection's registry id
 * @returns The mapped cases plus loading, error and refetch
 */
export const useEhrReviewQueue = (ehrConnectionId?: string | null) => {
  const response = useListEhrPatientLinksForReviewQuery({
    variables: { input: { ehr_connection_id: ehrConnectionId ?? '' } },
    skip: !ehrConnectionId,
    // Cases arrive from QIE in the background, so a remount MUST go to the
    // network rather than trust the cache.
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const data = useMemo(
    () =>
      (response.data?.listEhrPatientLinksForReview ?? []).map(mapEhrReviewCase),
    [response.data]
  );
  return {
    data,
    loading: response.loading,
    error: response.error,
    refetch: () => {
      void response.refetch();
    },
  };
};

/**
 * Approves a case against one candidate, or rejects it. Refreshes the queue
 * and the Patients tab.
 * @returns The review function, resolving true on success, and a loading flag
 */
export const useReviewEhrPatientLink = (): [
  (
    linkId: string,
    decision: 'approve' | 'reject',
    externalPatientId?: string
  ) => Promise<boolean>,
  boolean,
] => {
  const handleError = useWriteErrorHandler();
  const [mutate, { loading }] = useReviewEhrPatientLinkMutation({
    // getEhrSyncConnection carries review_count for the tab label.
    refetchQueries: [
      'listEhrPatientLinksForReview',
      'ehrSyncPatientLinks',
      'getEhrSyncConnection',
    ],
    awaitRefetchQueries: true,
  });
  const review = (
    linkId: string,
    decision: 'approve' | 'reject',
    externalPatientId?: string
  ) =>
    runWrite(
      () =>
        mutate({
          variables: {
            input: {
              link_id: linkId,
              decision:
                decision === 'approve'
                  ? EhrPatientReviewDecision.Approve
                  : EhrPatientReviewDecision.Reject,
              external_patient_id:
                decision === 'approve' ? externalPatientId : undefined,
            },
          },
        }),
      (data) => Boolean(data.reviewEhrPatientLink),
      handleError
    );
  return [review, loading];
};
